import React from 'react';
import {Button, Drawer, Form, Input, Select} from 'antd';
import {BaseError} from '../../types/apiTypes';
import {renderCheckableAddressTypeTag} from '../../helpers/contact';
import {AddressFormDrawerProps, ContactData, ContactMethod} from '../../types/contactMethodTypes';
import {messageType, showMessages} from '../../helpers/message';
import {useAddContactMethodMutation, useUpdateContactMethodMutation} from '../../store/api/contactMethodsApi';
import {useGetCountries} from '../../hooks/useGetCountries';
import {isTwoArrayIdentical} from '../../helpers/misc';
import {nothingToUpdate} from '../../helpers/text';

import './AddressFormDrawer.less';

const AddressFormDrawer = ({
                               contactableID,
                               contactableType,
                               address,
                               drawerText,
                               isDrawerVisible,
                               setDrawerVisible
                           }: AddressFormDrawerProps) => {
    const [form] = Form.useForm();

    const [currentAddress, setCurrentAddress] = React.useState<ContactData | null>(null);
    const [checked, setChecked] = React.useState({
        mailing: false,
        billing: false,
        legal: false,
        home: false,
    });
    const mandatoryTypes = address?.hasMandatoryType ? address.mandatoryTypes : [];

    const [addContactMethod, {
        isLoading: addLoading,
        isSuccess: addDataSuccess,
        isError: addError,
        error: addErrorData,
        data: addData,
        reset: addReset
    }] =
        useAddContactMethodMutation();

    const [updateContactMethod, {
        isLoading: updateLoading,
        isSuccess: updateDataSuccess,
        isError: updateError,
        error: updateErrorData,
        data: updateData,
        reset: updateReset
    }] =
        useUpdateContactMethodMutation();

    const {countries, provinces, setCurrentIndex, countriesLoading} = useGetCountries(
        address?.country
    );

    React.useEffect(() => {
        if (addDataSuccess && addData) {
            showMessages('Address successfully added.', messageType.success);
            setDrawerVisible(false);
            addReset();
        } else if (updateDataSuccess && updateData) {
            showMessages('Address successfully updated.', messageType.success);
            setDrawerVisible(false);
            updateReset();
        } else if (addError && addErrorData) {
            showMessages((addErrorData as BaseError).data?.message, messageType.error);
            addReset();
        } else if (updateError && updateErrorData) {
            showMessages((updateErrorData as BaseError).data?.message, messageType.error);
            updateReset();
        }
    }, [addDataSuccess, addData, updateDataSuccess, updateData, addError, addErrorData, updateError, updateErrorData, setDrawerVisible, addReset, updateReset]);

    React.useEffect(() => {
        if (isDrawerVisible) {
            // Set initial state on drawer open.
            if (address?.type) {
                setChecked({
                    home: address.type.includes('home'),
                    mailing: address.type.includes('mailing'),
                    legal: address.type.includes('legal'),
                    billing: address.type.includes('billing'),
                });
                setCurrentAddress(address);
            }

            if (drawerText.includes('Edit') && address && address.id) {
                const initialValues = {
                    'Street Address 1': address.street_address_1 || '',
                    'Street Address 2': address.street_address_2 || '',
                    'City': address.city || '',
                    'Province': address.province || '',
                    'Postal Code': address.postal_code || '',
                    'Country': address.country || '',
                };
                form.setFieldsValue(initialValues);

                setChecked({
                    home: address.type.includes('home'),
                    mailing: address.type.includes('mailing'),
                    legal: address.type.includes('legal'),
                    billing: address.type.includes('billing'),
                });
                setCurrentAddress(address);
            }
        } else if (!isDrawerVisible) {
            // Reset form state when drawer is closed.
            setCurrentAddress(null);
            form.resetFields();
        }
    }, [address, drawerText, form, isDrawerVisible, setCurrentIndex]);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;

        setCurrentAddress((prevState) => ({
            ...(prevState as ContactData),
            [name]: value,
        }));
    };

    const sanityCheck = (state: ContactData): boolean => {
        const {
            street_address_1,
            street_address_2,
            city,
            province,
            country,
            postal_code,
            type
        } = state;

        if (
            street_address_1 !== address?.street_address_1 ||
            street_address_2 !== address?.street_address_2 ||
            city !== address?.city ||
            province !== address?.province ||
            country !== address?.country ||
            postal_code !== address?.postal_code ||
            !isTwoArrayIdentical(type, address?.type)
        ) {
            return true;
        }

        showMessages(nothingToUpdate, messageType.error);
        return false;
    };

    const handleFinish = () => {
        if (!currentAddress) return;

        const types: string[] = [];
        if (checked.home) types.push('home');
        if (checked.mailing) types.push('mailing');
        if (checked.legal) types.push('legal');
        if (checked.billing) types.push('billing');

        const streetAddress2 = currentAddress.street_address_2 || '';

        const data: ContactData = {
            ...currentAddress,
            type: types,
            contactable_id: contactableID,
            contactable_type: contactableType,
            method: ContactMethod.address,
            street_address_2: streetAddress2,
        };

        if (drawerText.includes('Edit') && address) {
            if (!sanityCheck(data)) return;
            updateContactMethod({...data, id: address.id});
        } else {
            addContactMethod(data);
        }
    };

    const validatePostalCode = (_: any, value: string) => {
        const country = currentAddress?.country;
        let postalOrZip = 'Postal';
        let postalRegex;

        switch (country) {
            case 'Canada':
                postalRegex = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;
                break;
            case 'United States':
                postalRegex = /^\d{5}(?:[-\s]\d{4})?$/i;
                postalOrZip = 'Zip';
        }

        if (!value) {
            return Promise.reject(`'${postalOrZip} Code' is required.`)
        } else if (postalRegex && !postalRegex.test(value)) {
            return Promise.reject(`Please input a valid ${postalOrZip} Code.`);
        } else {
            return Promise.resolve();
        }
    };

    const onFinish = () => {
        form.validateFields().then(() => {
            handleFinish();
        });
    };

    const renderTypeTags = (isBusiness: boolean) => {
        return (
            <>
                {renderCheckableAddressTypeTag(
                    'mailing',
                    checked.mailing,
                    setChecked,
                    mandatoryTypes)}

                {!isBusiness && renderCheckableAddressTypeTag(
                    'home',
                    checked.home,
                    setChecked,
                    mandatoryTypes)}

                {isBusiness && renderCheckableAddressTypeTag(
                    'billing',
                    checked.billing,
                    setChecked,
                    mandatoryTypes)}

                {isBusiness && renderCheckableAddressTypeTag(
                    'legal',
                    checked.legal,
                    setChecked,
                    mandatoryTypes)}
            </>
        )
    };

    return (
        <Drawer title={drawerText} onClose={() => setDrawerVisible(false)} open={isDrawerVisible}>
            <Form
                form={form}
                name="address"
                layout="vertical"
                onFinish={onFinish}
            >
                <Form.Item
                    name="Street Address 1"
                    label="Street Address 1"
                    rules={[{
                        type: 'string',
                        required: true,
                        whitespace: true,
                        message: "'Street Address' is required."
                    }]}
                >
                    <Input
                        name="street_address_1"
                        placeholder="Street and number, P.O.box."
                        onChange={onChange}
                    />
                </Form.Item>
                <Form.Item
                    name="Street Address 2"
                    label="Street Address 2"
                    rules={[{type: 'string'}]}
                >
                    <Input
                        name="street_address_2"
                        placeholder="Apartment, suite, unit, building, floor, etc."
                        onChange={onChange}
                    />
                </Form.Item>
                <Form.Item
                    name="City"
                    label="City"
                    rules={[
                        {
                            type: 'string',
                            required: true,
                        },
                        {
                            message: 'Please enter valid characters with letters only.',
                            pattern: /^[a-zA-Z\s,'-]*$/,
                        }
                    ]}
                >
                    <Input name="city" onChange={onChange}/>
                </Form.Item>
                <Form.Item
                    name="Province"
                    label={currentAddress?.country === 'United States' ? 'State' : 'Province'}
                    rules={[{
                        // Province is required if provinces exist on the country.
                        required: provinces ? !!provinces.length : !!form.getFieldValue('country'),
                        message: `${currentAddress?.country === 'United States' ? "'State'" : "'Province'"} is required.`,
                    }]}
                >
                    <Select
                        placeholder={!form.getFieldValue('country') ? 'Please select a Country' : ''}
                        loading={countriesLoading}
                        dropdownMatchSelectWidth={false}
                        value={address?.province}
                        options={provinces}
                        onChange={(value) => {
                            setCurrentAddress((prevState) => ({
                                ...(prevState as ContactData),
                                province: value,
                            }));
                        }}
                    />
                </Form.Item>
                <Form.Item
                    name="Postal Code"
                    label={currentAddress?.country === 'United States' ? 'Zip Code' : 'Postal Code'}
                    dependencies={['Country']}
                    rules={[{type: 'string', required: true, validator: validatePostalCode}]}
                >
                    <Input name="postal_code" onChange={onChange}/>
                </Form.Item>
                <Form.Item
                    name="Country"
                    label="Country"
                    dependencies={['Postal Code']}
                    rules={[{required: true}]}
                >
                    <Select
                        loading={countriesLoading}
                        dropdownMatchSelectWidth={false}
                        value={address?.country}
                        options={countries}
                        onChange={(value) => {
                            const countryIndex = countries?.findIndex((c) => c.value === value) ?? null;
                            setCurrentIndex({
                                country: countryIndex,
                                province: null,
                            });

                            // Auto fill BC for Canada.
                            let newProvince = '';
                            if (value === 'Canada') newProvince = 'British Columbia';
                            form.setFieldValue('Province', newProvince);

                            setCurrentAddress((prevState) => ({
                                ...(prevState as ContactData),
                                country: value,
                                province: newProvince
                            }));
                        }}
                    />
                </Form.Item>
                <Form.Item label="Type">
                    {renderTypeTags(contactableType === 'company')}
                </Form.Item>
                <Form.Item>
                    <Button onClick={() => setDrawerVisible(false)}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={addLoading || updateLoading}>
                        {drawerText.includes('Edit') ? 'Save' : 'Add'}
                    </Button>
                </Form.Item>
            </Form>
        </Drawer>
    );
};

export default AddressFormDrawer;
